import { NgModule } from '@angular/core';
import { ROUTES, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './auth/auth-guards/auth.guard';
import {
  XgsGuardGuard,
  CanDeactivateGuard
} from './auth/auth-guards/xgs-guard.guard';
import { AuthorizeComponent } from './authorize/authorize.component';
import { LandingComponent } from './landing/landing.component';
import { LoginComponent } from './login/login.component';
import { SupersetComponent } from './superset/superset.component';

const routes: Routes = [
  {
    path: 'landing',
    component: LandingComponent,
    pathMatch: 'full',
    canActivate: [XgsGuardGuard]
  },
  { path: '', redirectTo: '/login', pathMatch: 'full' },
  { path: 'home', redirectTo: 'landing', pathMatch: 'full' },
  { path: 'login', component: LoginComponent, pathMatch: 'full' },
  { path: 'authorize', component: AuthorizeComponent, pathMatch: 'full' },
  {
    path: 'user',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./user-management/user/user.module').then(m => {
        return m.UserModule;
      });
    }
  },
  {
    path: 'role',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./user-management/role/role.module').then(m => {
        return m.RoleModule;
      });
    }
  },

  {
    path: 'pipediver-dashboard',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./pipediver-dashboard/pipediver-dashboard.module').then(
        m => {
          return m.PipediverDashboardModule;
        }
      );
    }
  },

  {
    path: 'superset',
    component: SupersetComponent,
    pathMatch: 'full',
    canActivate: [XgsGuardGuard, AuthGuard]
  },
  {
    path: 'gsodatawarehouse',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./GSO-Datawarehouse/gso-datawarehouse.module').then(m => {
        return m.GSODatawarehouseModule;
      });
    }
  },
  {
    path: 'data-import',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./data-import/data-import.module').then(m => {
        return m.DataImportModule;
      });
    }
  },
  {
    path: 'meter-history',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./meter-history/meter-history.module').then(m => {
        return m.MeterHistoryModule;
      });
    }
  },
  {
    path: 'sql-generator',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./sql-generator/sql-generator.module').then(m => {
        return m.SqlGeneratorModule;
      });
    }
  },
  {
    path: 'managedservices',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./managed-services/managed-services.module').then(m => {
        return m.ManagedServicesModule;
      });
    }
  },
  {
    path: 'scheduler',
    canLoad: [XgsGuardGuard],
    canDeactivate: [CanDeactivateGuard],
    loadChildren: async () => {
      return import('./scheduler/scheduler.module').then(m => {
        return m.SchedulerModule;
      });
    }
  },
  {
    path: 'sensus-genai-assistant',
    canLoad: [XgsGuardGuard],
    loadComponent: () =>
      import('./fcp-chatbot/fcp-chatbot.component').then(
        m => m.FcpChatbotComponent
      )
  },
  {
    path: 'backspools',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./backspool/backspool.module').then(m => {
        return m.BackspoolModule;
      });
    }
  },
  {
    // Public page for on-prem utilities: no login, access is by link token.
    // AppComponent hides the app shell for this route.
    path: 'backspool-upload/:token',
    loadComponent: () =>
      import(
        './backspool/backspool-public-upload/backspool-public-upload.component'
      ).then(m => m.BackspoolPublicUploadComponent)
  },
  {
    path: 'spectrum-planning',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./spectrum-planning/spectrum-planning.module').then(m => {
        return m.SpectrumPlanningModule;
      });
    }
  },
  {
    path: 'na2w-sales-tool',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./na2w-sales-tool/na2w-sales-tool.module').then(m => {
        return m.Na2wSalesToolModule;
      });
    }
  },
  {
    path: 'quicksight-dashboards',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./quicksight-dashboards/quicksight-dashboards.module').then(
        m => {
          return m.QuicksightDashboardsModule;
        }
      );
    }
  },
  {
    path: 'fst-dashboard',
    canLoad: [XgsGuardGuard],
    canActivate: [XgsGuardGuard, AuthGuard],
    data: { permission: 'datalake:reports' },
    loadComponent: () =>
      import('./fst-dashboard/fst-dashboard.component').then(
        m => m.FstDashboardComponent
      )
  },
  {
    path: 'iperl-customer-alarms',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./iperl-customer-alarms/iperl-customer-alarms.module').then(m => {
        return m.IperlCustomerAlarmsModule;
      });
    }
  },
  {
    path: 'fst-fuel-prediction',
    canLoad: [XgsGuardGuard],
    canActivate: [XgsGuardGuard, AuthGuard],
    data: { permission: 'datalake:reports' },
    loadComponent: () =>
      import('./fst-fuelprediction/fst-fuelprediction.component').then(
        m => m.FstFuelPredictionComponent
      )
  },
  {
    path: 'map-dashboard',
    canLoad: [XgsGuardGuard],
    loadChildren: async () => {
      return import('./map-dashboard/map-dashboard.module').then(m => {
        return m.MapDashboardModule;
      });
    }
  },
  {
    path: '**',
    redirectTo: 'landing'
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot([], {
      enableTracing: false
    })
  ],
  exports: [RouterModule],
  providers: [
    {
      provide: ROUTES,
      useFactory: () => {
        const finalRoutes: Routes = [];

        return [...finalRoutes, ...routes];
      },
      multi: true
    }
  ]
})
export class AppRoutingModule {}
